import { errorText } from '../../../../lib/apiErrors';
import { useWikiMutation, useWikiQuery, wikiKeys } from '../../shared/useWikiQueries';

/**
 * Data for the AI Brain shell + Criteria tree (contracts/BE-brain.md).
 *
 * Query keys (FE-brain-ops: share these, never hand-build a Brain key):
 *   ['wiki','brain','tree']    GET /api/wiki/brain/tree
 *   ['wiki','brain','health']  GET /api/wiki/brain/health  — header JEV pill + the
 *                              always-on Review-queue count (`pending_count`)
 * Every tree write invalidates the whole 'brain' scope (tree, health, proposals)
 * plus the nav counts and Home callout, so the tab count, the nav badge and the
 * Review queue follow a flag-weak or a delete.
 */

export const BRAIN_INVALIDATE = Object.freeze(['brain', 'counts', 'home']);

export const brainKeys = Object.freeze({
    tree: () => wikiKeys.brain('tree'),
    health: () => wikiKeys.brain('health'),
});

export function useBrainTreeQuery(opts = {}) {
    return useWikiQuery(brainKeys.tree(), '/wiki/brain/tree', opts);
}

export function useBrainHealth(opts = {}) {
    return useWikiQuery(brainKeys.health(), '/wiki/brain/health', opts);
}

/** Pending proposals from the health payload (both names are served). */
export function brainPendingCount(health) {
    const n = Number(health?.pending_count ?? health?.pending_proposals ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

const MACHINE_CODES = Object.freeze({
    brain_node_not_found: 'This check no longer exists. The tree has been refreshed.',
    wiki_org_required: 'Your account is not linked to a workspace.',
});

/**
 * A Brain API error as a sentence to show verbatim: the server's `detail`
 * sentence (422/409), `detail.message` (plan-gated 403), a mapped machine code,
 * else the generic fallback.
 */
export function brainErrorText(error) {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && MACHINE_CODES[detail]) return MACHINE_CODES[detail];
    if (typeof detail === 'string' && /^Permission denied/.test(detail)) {
        return 'This needs the Manage AI Brain permission. Wiki admins have it.';
    }
    return errorText(error, "That didn't work. Please try again.");
}

const enc = (id) => encodeURIComponent(String(id));

/**
 * Per-node lifecycle writes (contract §3.3). Each resolves with the API body;
 * the 'brain' scope is refetched before `mutateAsync` resolves.
 */
export function useBrainNodeActions() {
    const opts = { invalidate: BRAIN_INVALIDATE };
    const replay = useWikiMutation((client, id) => client.post(`/wiki/brain/nodes/${enc(id)}/replay`, {}), opts);
    const activate = useWikiMutation((client, id) => client.post(`/wiki/brain/nodes/${enc(id)}/activate`, {}), opts);
    const deactivate = useWikiMutation((client, id) => client.post(`/wiki/brain/nodes/${enc(id)}/deactivate`, {}), opts);
    const flagWeak = useWikiMutation((client, id) => client.post(`/wiki/brain/nodes/${enc(id)}/flag-weak`, {}), opts);
    const remove = useWikiMutation((client, id) => client.delete(`/wiki/brain/nodes/${enc(id)}`), opts);
    return { replay, activate, deactivate, flagWeak, remove };
}

/** The new-node modal's write: `{path, body}` from `brainNewRequest`. */
export function useBrainCreate() {
    return useWikiMutation((client, { path, body }) => client.post(path, body), { invalidate: BRAIN_INVALIDATE });
}
